import { SAMPLE_FROM_SCALARS } from '@/services/calculationRunner.constants';
import type { StoredStatistic } from '@/tools/StatisticSelector.molecule';
import type { Optional } from '@/utilities.types';

/**
 * Compute the parameterized stat from the statistic collector
 *
 * @param {StoredStatistic} statistic - The statistic object
 * @param {string} statistic.key - The key into the SAMPLES_FROM_SCALARS.VALUE_METHOD object
 * @param {string} statistic.timeUnits - for stats that are outputInTimeUnits=true, this is the selected time unit
 * @param {number} statistic.percentile - for stats that needsPercentile=true, this is the specified percentile
 *
 * @return The formula fragment that is usable in the aggregate operator.
 *
 * @throws TypeError
 */
export function getStatisticFragment(statistic: StoredStatistic): string {
  const statObject = SAMPLE_FROM_SCALARS.VALUE_METHODS.find((valueMethod) => valueMethod.key === statistic.key);
  if (statObject != null) {
    return statObject.stat
      .replace('$unit', `"${statistic.timeUnits}"`)
      .replace('$percentile', String(statistic.percentile))
      .replace('$multiplier', String(statistic.multiplier));
  }

  throw new TypeError(`Unknown statistic key "${statistic.key}"`);
}

/**
 * Returns the translation key for the long title of a statistic, e.g. `percentDuration` gives
 * `STATISTICS.PERCENT_DURATION.LONG`. Unknown keys fall back to the upper-cased key so they still produce a key of
 * the same shape.
 */
export function getStatisticTitleKey(statisticKey: string): string {
  const statObject = SAMPLE_FROM_SCALARS.VALUE_METHODS.find((valueMethod) => valueMethod.key === statisticKey);

  return statObject?.title ?? `STATISTICS.${statisticKey.toUpperCase()}.LONG`;
}

/**
 * Parses a statistic formula fragment (e.g. `totalDuration("min")`) back into a `StoredStatistic`.
 */
export function getStatisticFromFragment(
  statisticFragment: string,
): Optional<StoredStatistic, 'timeUnits'> | undefined {
  const result = /^(\w*)\(['"]?(\w*)?['"]?\)$/.exec(statisticFragment);
  const fragFuncName = result?.[1];
  const stat = fragFuncName
    ? SAMPLE_FROM_SCALARS.VALUE_METHODS.find((valueMethod) => valueMethod.stat.startsWith(fragFuncName))
    : undefined;
  if (stat) {
    const statData = { key: stat.key };
    if (stat.needsPercentile) {
      const percentile = parseInt(result?.[2] ?? '', 10);
      Object.assign(statData, {
        percentile: Number.isNaN(percentile) ? null : percentile,
      });
    } else if (stat.outputInTimeUnits) {
      Object.assign(statData, { timeUnits: result?.[2] });
    } else if (stat.needsMultiplier) {
      const multiplier = parseFloat(result?.[2] ?? '');
      Object.assign(statData, {
        multiplier: Number.isNaN(multiplier) ? null : multiplier,
      });
    }

    return statData;
  }
}
